import React, { useState } from 'react';
import { useForm, ValidationError } from '@formspree/react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { corporateTheme } from '../themes/corporate';

const ContactContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
  text-align: center;
`;

const Title = styled(motion.h2)`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: ${corporateTheme.fontWeights.bold};
  color: ${corporateTheme.textPrimary};
  text-align: center;
  margin-bottom: 2rem;
  background: ${corporateTheme.primaryGradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-family: ${corporateTheme.fontFamily};
`;

const Subtitle = styled(motion.p)`
  font-size: clamp(1.2rem, 3vw, 1.8rem);
  color: ${corporateTheme.textSecondary};
  margin-bottom: 3rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  font-family: ${corporateTheme.fontFamily};
`;

const CTASection = styled(motion.div)`
  background: rgba(37, 99, 235, 0.05);
  backdrop-filter: blur(10px);
  border-radius: ${corporateTheme.borderRadius.xl};
  padding: 4rem;
  border: 1px solid rgba(37, 99, 235, 0.15);
  box-shadow: ${corporateTheme.cardShadow};
  margin-bottom: 4rem;
`;

const CTATitle = styled.h3`
  font-size: 2.5rem;
  font-weight: ${corporateTheme.fontWeights.bold};
  color: ${corporateTheme.primary};
  margin-bottom: 2rem;
  font-family: ${corporateTheme.fontFamily};
`;

const CTAText = styled.p`
  font-size: 1.3rem;
  color: ${corporateTheme.textPrimary};
  margin-bottom: 3rem;
  line-height: 1.6;
  font-family: ${corporateTheme.fontFamily};
`;



const NextStepsSection = styled(motion.div)`
  background: ${corporateTheme.cardBackground};
  backdrop-filter: blur(10px);
  border-radius: ${corporateTheme.borderRadius.xl};
  padding: 3rem;
  border: 1px solid ${corporateTheme.cardBorder};
  box-shadow: ${corporateTheme.cardShadow};
  margin-bottom: 4rem;
`;

const NextStepsTitle = styled.h3`
  font-size: 2rem;
  font-weight: ${corporateTheme.fontWeights.semibold};
  color: ${corporateTheme.primary};
  margin-bottom: 2rem;
  font-family: ${corporateTheme.fontFamily};
`;

const StepsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  text-align: left;
`;

const Step = styled(motion.div)`
  background: ${corporateTheme.cardBackground};
  border-radius: ${corporateTheme.borderRadius.lg};
  padding: 2rem;
  border: 1px solid ${corporateTheme.cardBorder};
  box-shadow: ${corporateTheme.cardShadow};
`;

const StepNumber = styled.div`
  width: 40px;
  height: 40px;
  background: ${corporateTheme.primaryGradient};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: ${corporateTheme.background};
  margin-bottom: 1rem;
  font-family: ${corporateTheme.fontFamily};
`;

const StepTitle = styled.h4`
  font-size: 1.3rem;
  color: ${corporateTheme.textPrimary};
  margin-bottom: 1rem;
  font-weight: ${corporateTheme.fontWeights.semibold};
  font-family: ${corporateTheme.fontFamily};
`;

const StepDescription = styled.p`
  color: ${corporateTheme.textSecondary};
  line-height: 1.6;
  font-family: ${corporateTheme.fontFamily};
`;

const ContactInfo = styled(motion.div)`
  background: ${corporateTheme.cardBackground};
  backdrop-filter: blur(10px);
  border-radius: ${corporateTheme.borderRadius.xl};
  padding: 2rem;
  border: 1px solid ${corporateTheme.cardBorder};
  box-shadow: ${corporateTheme.cardShadow};
`;

const ContactTitle = styled.h4`
  font-size: 1.5rem;
  color: ${corporateTheme.primary};
  margin-bottom: 1.5rem;
  font-weight: ${corporateTheme.fontWeights.semibold};
  font-family: ${corporateTheme.fontFamily};
`;

const ContactDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
`;

const ContactItem = styled.div`
  text-align: center;
`;

const ContactLabel = styled.div`
  font-size: 0.9rem;
  color: ${corporateTheme.textSecondary};
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-family: ${corporateTheme.fontFamily};
`;

const ContactValue = styled.div`
  font-size: 1.1rem;
  color: ${corporateTheme.textPrimary};
  font-weight: ${corporateTheme.fontWeights.medium};
  font-family: ${corporateTheme.fontFamily};
`;

const ContactForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  text-align: left;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const FormLabel = styled.label`
  font-size: 1rem;
  color: ${corporateTheme.textPrimary};
  font-weight: ${corporateTheme.fontWeights.medium};
  margin-bottom: 0.5rem;
  font-family: ${corporateTheme.fontFamily};
`;

const FormInput = styled.input`
  padding: 1rem 1.5rem;
  background: ${corporateTheme.cardBackground};
  border: 1px solid ${corporateTheme.cardBorder};
  border-radius: ${corporateTheme.borderRadius.lg};
  color: ${corporateTheme.textPrimary};
  font-size: 1rem;
  font-family: ${corporateTheme.fontFamily};
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${corporateTheme.primary};
    background: rgba(37, 99, 235, 0.05);
  }
  
  &::placeholder {
    color: ${corporateTheme.textSecondary};
  }
`;

const FormSelect = styled.select`
  padding: 1rem 1.5rem;
  background: ${corporateTheme.cardBackground};
  border: 1px solid ${corporateTheme.cardBorder};
  border-radius: ${corporateTheme.borderRadius.lg};
  color: ${corporateTheme.textPrimary};
  font-size: 1rem;
  font-family: ${corporateTheme.fontFamily};
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${corporateTheme.primary};
    background: rgba(37, 99, 235, 0.05);
  }
  
  option {
    background: ${corporateTheme.cardBackground};
    color: ${corporateTheme.textPrimary};
  }
`;

const FormTextArea = styled.textarea`
  padding: 1rem 1.5rem;
  background: ${corporateTheme.cardBackground};
  border: 1px solid ${corporateTheme.cardBorder};
  border-radius: ${corporateTheme.borderRadius.lg};
  color: ${corporateTheme.textPrimary};
  font-size: 1rem;
  font-family: ${corporateTheme.fontFamily};
  resize: vertical;
  min-height: 120px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${corporateTheme.primary};
    background: rgba(37, 99, 235, 0.05);
  }
  
  &::placeholder {
    color: ${corporateTheme.textSecondary};
  }
`;

const SubmitButton = styled(motion.button)`
  background: ${corporateTheme.primaryGradient};
  color: ${corporateTheme.background};
  border: none;
  padding: 1.2rem 2rem;
  font-size: 1.1rem;
  font-weight: ${corporateTheme.fontWeights.semibold};
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: ${corporateTheme.fontFamily};
  box-shadow: ${corporateTheme.cardShadow};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 15px 40px rgba(37, 99, 235, 0.4);
    background: linear-gradient(135deg, ${corporateTheme.primaryLight} 0%, ${corporateTheme.primary} 100%);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const FormNote = styled.p`
  font-size: 0.9rem;
  color: ${corporateTheme.textSecondary};
  text-align: center;
  margin-top: 1rem;
  font-family: ${corporateTheme.fontFamily};
`;

const SuccessMessage = styled(motion.div)`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  padding: 2rem;
  border-radius: 15px;
  text-align: center;
  margin-bottom: 2rem;
  box-shadow: 0 10px 30px rgba(16, 185, 129, 0.3);
`;

const SuccessTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const SuccessText = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  margin: 0;
`;

const ErrorMessage = styled(motion.div)`
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  padding: 1.5rem;
  border-radius: 15px;
  text-align: center;
  margin-bottom: 2rem;
  box-shadow: 0 10px 30px rgba(239, 68, 68, 0.3);
`;

const ContactSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [state, handleSubmit] = useForm("xpwrvwnw");

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    interest: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const formDataToSend = new FormData(e.target);
    
    // Add custom fields
    formDataToSend.append('_subject', 'GrowTech Partnership Inquiry');
    formDataToSend.append('_cc', 'info@growtech.cc');
    formDataToSend.append('_next', 'javascript:void(0)'); // Disable redirect
    
    await handleSubmit(formDataToSend);
    
    // Reset form on success
    if (state.succeeded) {
      setFormData({
        name: '',
        email: '',
        company: '',
        interest: '',
        message: ''
      });
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const nextSteps = [
    {
      number: "1",
      title: "Initial Discussion",
      description: "Schedule a detailed presentation to discuss the opportunity, market potential, and strategic fit."
    },
    {
      number: "2",
      title: "Due Diligence",
      description: "Comprehensive review of business plan, market research, and financial projections with your team."
    },
    {
      number: "3",
      title: "Partnership Agreement",
      description: "Finalize investment terms, governance structure, and launch timeline for the venture."
    },
    {
      number: "4",
      title: "Execution Launch",
      description: "Begin immediate implementation with team recruitment, platform development, and market entry."
    }
  ];

  return (
    <ContactContainer ref={ref}>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
      >
        <Title variants={itemVariants}>
          Ready to Transform Eastern Asian Affiliate Marketing?
        </Title>

        <Subtitle variants={itemVariants}>
          Join us in building the next generation affiliate marketing platform 
          with proprietary content assets and data-driven strategies.
        </Subtitle>

        <CTASection variants={itemVariants}>
          <CTATitle>Get in Touch</CTATitle>
          <CTAText>
            Ready to partner with GrowTech? Send us a message and we'll get back to you within 24 hours.
          </CTAText>
          
          <ContactForm
            onSubmit={onSubmit}
          >
            {state.succeeded && (
              <SuccessMessage
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <SuccessTitle>✨ Thank You!</SuccessTitle>
                <SuccessText>
                  Your message has been sent successfully! We'll get back to you within 24 hours to discuss this partnership opportunity.
                </SuccessText>
              </SuccessMessage>
            )}
            
            {state.errors && state.errors.length > 0 && (
              <ErrorMessage
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                There was an error sending your message. Please try again or email us directly at info@growtech.cc
              </ErrorMessage>
            )}
            
                          <FormRow>
                <FormGroup>
                  <FormLabel htmlFor="name">Full Name *</FormLabel>
                  <FormInput
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your full name"
                    required
                  />
                  <ValidationError prefix="Name" field="name" errors={state.errors} />
                </FormGroup>
                <FormGroup>
                  <FormLabel htmlFor="email">Email *</FormLabel>
                  <FormInput
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your.email@company.com"
                    required
                  />
                  <ValidationError prefix="Email" field="email" errors={state.errors} />
                </FormGroup>
              </FormRow>
              
              <FormRow>
                <FormGroup>
                  <FormLabel htmlFor="company">Company</FormLabel>
                  <FormInput
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Your company name"
                  />
                  <ValidationError prefix="Company" field="company" errors={state.errors} />
                </FormGroup>
                <FormGroup>
                  <FormLabel htmlFor="interest">Interest *</FormLabel>
                  <FormSelect
                    id="interest"
                    name="interest"
                    value={formData.interest}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select your interest</option>
                    <option value="partnership">Partnership Opportunity</option>
                    <option value="investment">Investment Discussion</option>
                    <option value="collaboration">Business Collaboration</option>
                    <option value="other">Other</option>
                  </FormSelect>
                  <ValidationError prefix="Interest" field="interest" errors={state.errors} />
                </FormGroup>
              </FormRow>
              
              <FormGroup>
                <FormLabel htmlFor="message">Message *</FormLabel>
                <FormTextArea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  placeholder="Tell us about your interest in GrowTech..."
                  required
                />
                <ValidationError prefix="Message" field="message" errors={state.errors} />
              </FormGroup>
              
              <SubmitButton
                type="submit"
                disabled={state.submitting}
                whileHover={{ scale: state.submitting ? 1 : 1.05 }}
                whileTap={{ scale: state.submitting ? 1 : 0.95 }}
              >
                {state.submitting ? 'Sending...' : 'Send Message'}
              </SubmitButton>
            
            <FormNote>
              * Required fields. We'll respond within 24 hours.
            </FormNote>
          </ContactForm>
        </CTASection>

        <NextStepsSection variants={itemVariants}>
          <NextStepsTitle>Next Steps</NextStepsTitle>
          <StepsList>
            {nextSteps.map((step, index) => (
              <Step
                key={index}
                variants={itemVariants}
                whileHover={{ scale: 1.02, y: -3 }}
                transition={{ duration: 0.3 }}
              >
                <StepNumber>{step.number}</StepNumber>
                <StepTitle>{step.title}</StepTitle>
                <StepDescription>{step.description}</StepDescription>
              </Step>
            ))}
          </StepsList>
        </NextStepsSection>

        <ContactInfo variants={itemVariants}>
          <ContactTitle>Contact Information</ContactTitle>
          <ContactDetails>
            <ContactItem>
              <ContactLabel>Company</ContactLabel>
              <ContactValue>GrowTech Ltd</ContactValue>
            </ContactItem>
            <ContactItem>
              <ContactLabel>Email</ContactLabel>
              <ContactValue>info@growtech.cc</ContactValue>
            </ContactItem>
            <ContactItem>
              <ContactLabel>Location</ContactLabel>
              <ContactValue>Hong Kong</ContactValue>
            </ContactItem>
            <ContactItem>
              <ContactLabel>Investment</ContactLabel>
              <ContactValue>€12M Total</ContactValue>
            </ContactItem>
            <ContactItem>
              <ContactLabel>Expected ROI</ContactLabel>
              <ContactValue>25-40% Annual</ContactValue>
            </ContactItem>
            <ContactItem>
              <ContactLabel>Timeline</ContactLabel>
              <ContactValue>Launch in 6 Months</ContactValue>
            </ContactItem>
          </ContactDetails>
        </ContactInfo>
      </motion.div>
    </ContactContainer>
  );
};

export default ContactSection; 